"use client"

import { useParams } from "react-router-dom"
import { CardTitle } from "../ui/card"
import { CardContent } from "../ui/card"
import { CardHeader } from "../ui/card"
import { Card } from "../ui/card"
import { useState, useEffect } from "react"
import { Button } from "../ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion"
import { BookOpen, Calendar, Clock, Download, PlayCircle, Share, Star, User } from "lucide-react"
import { Label } from "../ui/label"
import { Input } from "../ui/input"
import { formationAPI } from "../../services/api"
import { inscriptionAPI } from "../../services/api"

// Type definitions
interface FormationCategory {
  name: string
}

interface Formation {
  id: number
  name?: string
  shortDescription?: string
  longDescription?: string
  price?: number
  currency?: string
  totalDuration?: number
  videoDescription?: string
  image?: string
  lastUpdate?: string
  FormationCategory?: FormationCategory
  formateur?: {
    id: number
    name: string
    photo: string | null
  } | null
}

interface FormationCounts {
  inscriptions: number
  chapitres: number
  ressources: number
}

interface FormationResponse {
  formation: Formation
  counts: FormationCounts
}

interface Chapitre {
  title: string
  durationMinutes: number
}

interface Section {
  sectionTitle: string
  durationMinutes: number
  chapitreCount: number
  chapitres: Chapitre[]
}

interface ApprendreItem {
  learningText: string
}

interface PublicCibleItem {
  targetText: string
}

interface Ressource {
  name: string
  description: string
  fileType: string
  filePath: string
}

interface FormationDetails {
  sections: Section[]
  apprendre: ApprendreItem[]
  publicCible: PublicCibleItem[]
  ressources: Ressource[]
  formateur?: {
    id: number
    name: string
    photo: string | null
    bio: string | null
    socialMedia: {
      facebook: string | null
      instagram: string | null
      linkedin: string | null
    } | null
  } | null
}

interface SimilarFormation {
  id: number
  name?: string
  image?: string
  totalDuration?: number
  FormationCategory?: FormationCategory
}

interface FormData {
  fullName: string
  email: string
  phoneNumber: string
}

export default function CourseDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [formation, setFormation] = useState<FormationResponse | null>(null)
  const [formationDetails, setFormationDetails] = useState<FormationDetails | null>(null)
  const [similarFormations, setSimilarFormations] = useState<SimilarFormation[]>([])
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phoneNumber: ''
  })
  const [submitting, setSubmitting] = useState(false)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleShareClick = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert("Le lien de la formation a été copié dans le presse-papiers !");
    } catch (err) {
      console.error("Erreur lors de la copie du lien :", err);
      alert("Une erreur est survenue lors de la copie du lien.");
    }
  };

  useEffect(() => {
    if (id) {
      fetchFormationData()
    }
  }, [id])

  const fetchFormationData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Convert string id to number for API calls
      const numericId = parseInt(id || '0', 10)
      if (isNaN(numericId)) {
        throw new Error('Invalid formation ID')
      }

      // Fetch all data in parallel
      const [formationRes, detailsRes, similarRes] = await Promise.allSettled([
        formationAPI.getById(numericId),
        formationAPI.getFormationDetails(numericId),
        formationAPI.getSimilarFormations(numericId)
      ])

      // Handle formation basic data
      if (formationRes.status === 'fulfilled') {
        setFormation(formationRes.value.data || formationRes.value)
      } else {
        console.error('Failed to fetch formation:', formationRes.reason)
      }

      // Handle formation details
      if (detailsRes.status === 'fulfilled') {
        setFormationDetails(detailsRes.value.data || detailsRes.value)
      } else {
        console.error('Failed to fetch formation details:', detailsRes.reason)
      }

      // Handle similar formations
      if (similarRes.status === 'fulfilled') {
        setSimilarFormations(similarRes.value.data || similarRes.value)
      } else {
        console.error('Failed to fetch similar formations:', similarRes.reason)
      }

    } catch (err) {
      setError('Une erreur est survenue lors du chargement des données.')
      console.error('Error fetching formation data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!formation?.formation?.id) {
      setErrorMessage("Erreur : Formation non trouvée")
      return
    }

    try {
      setSubmitting(true)
      setErrorMessage(null)

      const inscriptionData = {
        ...formData,
        formationId: formation.formation.id
      }

      await inscriptionAPI.create(inscriptionData)

      setFormData({ fullName: '', email: '', phoneNumber: '' })
      setErrorMessage("Votre demande d'inscription a été envoyée avec succès.")

    } catch (error: any) {
      const responseError = error?.response?.data?.error
      if (responseError) {
        setErrorMessage(responseError)
      } else {
        setErrorMessage("Une erreur est survenue lors de l'envoi de votre demande.")
      }
    } finally {
      setSubmitting(false)
    }
  }

  const formatDuration = (minutes: number | undefined): string => {
    if (!minutes) return '0 min'
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`
    }
    return `${mins}min`
  }

  const scrollToForm = () => {
    const formElement = document.getElementById('inscription-form')
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  if (loading) {
    return (
      <div className="container px-4 py-12 md:px-6 md:py-24 flex flex-col items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Chargement de la formation...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container px-4 py-12 md:px-6 md:py-24 flex flex-col items-center">
        <h1 className="text-2xl font-serif font-bold mb-4 text-red-600">Erreur</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">{error}</p>
        <Button onClick={() => window.location.reload()}>
          Réessayer
        </Button>
      </div>
    )
  }

  if (!formation?.formation) {
    return (
      <div className="container px-4 py-12 md:px-6 md:py-24 flex flex-col items-center">
        <h1 className="text-2xl font-serif font-bold mb-4">Formation non trouvée</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">La formation que vous recherchez n'existe pas.</p>
        <a href="/courses">
          <Button>Retour aux formations</Button>
        </a>
      </div>
    )
  }

  const course = formation.formation
  const counts = formation.counts
  const details = formationDetails

  return (
    <div className="flex flex-col min-h-screen">
      {/* Course Header */}
      <section className="w-full py-12 md:py-24 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container px-4 md:px-6">
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-12 items-center">
            <div className="flex flex-col justify-center space-y-4">
              <div className="space-y-2">
                {course.FormationCategory?.name && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                    {course.FormationCategory.name}
                  </span>
                )}
                <h1 className="text-3xl font-serif font-bold tracking-tighter sm:text-4xl md:text-5xl">
                  {course.name}
                </h1>
                <p className="max-w-[600px] text-gray-500 md:text-xl dark:text-gray-400">
                  {course.shortDescription}
                </p>
              </div>
              <div className="flex flex-wrap gap-4 mt-2">
                <div className="flex items-center">
                  <User className="w-5 h-5 text-gray-500 dark:text-gray-400 mr-1" />
                  <span>{counts?.inscriptions || 0} participant·e·s</span>
                </div>
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400 mr-1" />
                  <span>Dernière mise à jour: {course.lastUpdate ? new Date(course.lastUpdate).toLocaleDateString('fr-FR') : 'N/A'}</span>
                </div>
              </div>
              <div className="flex items-center mt-2">
                <img
                  src={course.formateur?.photo || "/avatar.png"}
                  alt={course.formateur?.name || "Elille academy"}
                  width={40}
                  height={40}
                  className="rounded-full mr-2"
                />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Formateur·rice</p>
                  <p className="font-medium">{course.formateur?.name || "Elille academy"}</p>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <div className="flex flex-col min-[400px]:flex-row justify-end">
                <Button className="px-8" onClick={scrollToForm}>
                  S'inscrire maintenant
                </Button>
              </div>
            </div>
          </div>
          <div className="mx-auto lg:ml-auto mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-8">
                <div className="relative rounded-xl overflow-hidden shadow-xl">
                  {isVideoPlaying && course.videoDescription ? (
                    // ✅ Show video only if it exists and user clicked play
                    <video
                      className="w-full aspect-video object-cover"
                      src={course.videoDescription} // ✅ Use full URL from backend
                      controls
                      onError={() => {
                        // ✅ If video fails to load, go back to image
                        setIsVideoPlaying(false);
                        console.error('Failed to load video');
                      }}
                    />
                  ) : (
                    <div className="relative">
                      <img
                        src={course.image || "/placeholder.svg"}
                        alt={course.name}
                        width={560}
                        height={315}
                        className="w-full aspect-video object-cover"
                      />
                      {/* ✅ Only show play button if video exists */}
                      {course.videoDescription && (
                        <div
                          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 cursor-pointer hover:bg-opacity-40 transition-all duration-300"
                          onClick={() => setIsVideoPlaying(true)}
                        >
                          <div className="bg-white/20 p-5 rounded-full backdrop-blur-sm">
                            <PlayCircle className="w-16 h-16 text-white" />
                          </div>
                          <span className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-md text-sm font-medium backdrop-blur-sm">
                            Regarder l'aperçu
                          </span>
                        </div>
                      )}
                      {/* ✅ Show indicator when no video is available */}
                      {!course.videoDescription && (
                        <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-md text-sm font-medium backdrop-blur-sm">
                          Image de couverture
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="lg:col-span-4">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-xl h-full">
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-3xl font-bold text-elille-green">
                      {course.price ? `${course.price}${course.currency}` : 'Gratuit'}
                    </span>
                    <Button
                      className="bg-elille-blue hover:bg-elille-blue/90"
                      onClick={scrollToForm}
                    >
                      S'inscrire
                    </Button>
                  </div>

                  <div className="space-y-5">
                    <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <Clock className="w-10 h-10 text-elille-blue p-2 bg-elille-blue/10 rounded-full mr-4" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Durée totale</p>
                        <p className="font-bold">{formatDuration(course.totalDuration)}</p>
                      </div>
                    </div>

                    <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <BookOpen className="w-10 h-10 text-elille-red p-2 bg-elille-red/10 rounded-full mr-4" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Chapitres</p>
                        <p className="font-bold">{counts?.chapitres || 0} leçons</p>
                      </div>
                    </div>

                    <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <Download className="w-10 h-10 text-elille-green p-2 bg-elille-green/10 rounded-full mr-4" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Ressources</p>
                        <p className="font-bold">{counts?.ressources || 0} téléchargeables</p>
                      </div>
                    </div>

                    <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <User className="w-10 h-10 text-elille-orange p-2 bg-elille-orange/10 rounded-full mr-4" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Participants</p>
                        <p className="font-bold">{counts?.inscriptions || 0} inscrits</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleShareClick}
                    >
                      <Share className="mr-2 h-4 w-4" />
                      Partager cette formation
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formulaire d'inscription */}
      <section id="inscription-form" className="w-full py-12 bg-white dark:bg-gray-800 border-t border-b">
        <div className="container px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col gap-4">
              <h2 className="text-2xl font-serif font-bold text-center">S'inscrire à cette formation</h2>
              <p className="text-center text-gray-500 dark:text-gray-400 mb-6">
                Remplissez ce formulaire pour vous inscrire à la formation "{course.name}". Nous vous contacterons pour
                confirmer votre inscription.
              </p>

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Nom complet</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      placeholder="Votre nom complet"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="votre.email@exemple.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber">Téléphone</Label>
                    <Input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      placeholder="+33 6 12 34 56 78"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#00088E] hover:bg-elille-blue/90"
                  disabled={submitting}
                >
                  {submitting ? 'Inscription en cours...' : 'S\'inscrire à la formation'}
                </Button>
                {errorMessage && (
                  <div className={`mt-4 p-4 rounded-md text-sm font-medium
                    ${errorMessage.includes("succès") ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                    {errorMessage}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Course Content */}
      <section className="w-full py-12 md:py-24">
        <div className="container px-4 md:px-6">
          <Tabs defaultValue="content" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="content">Contenu</TabsTrigger>
              <TabsTrigger value="description">Description</TabsTrigger>
              <TabsTrigger value="resources">Ressources</TabsTrigger>
              <TabsTrigger value="formateur">Formateur</TabsTrigger>
            </TabsList>

            <TabsContent value="content" className="mt-6">
              {details?.sections && details.sections.length > 0 ? (
                <div className="space-y-4">
                  <h2 className="text-2xl font-serif font-bold">Contenu de la formation</h2>
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
                    <span>
                      {details.sections.length} sections • {details.sections.reduce((acc: number, section: Section) => acc + section.chapitreCount, 0)} modules • {formatDuration(course.totalDuration)} au total
                    </span>
                  </div>
                  <Accordion type="single" collapsible className="w-full">
                    {details.sections.map((section: Section, index: number) => (
                      <AccordionItem key={index} value={`section-${index}`}>
                        <AccordionTrigger className="text-left">
                          <div>
                            <div className="font-medium">{section.sectionTitle}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {section.chapitreCount} modules • {formatDuration(section.durationMinutes)}
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul className="space-y-2">
                            {section.chapitres.map((chapitre: Chapitre, chapitreIndex: number) => (
                              <li
                                key={chapitreIndex}
                                className="flex items-center justify-between p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                              >
                                <div className="flex items-center">
                                  <PlayCircle className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
                                  <span>{chapitre.title}</span>
                                </div>
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  {formatDuration(chapitre.durationMinutes)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">Contenu de la formation non disponible</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="description" className="mt-6">
              <div className="space-y-4">
                <h2 className="text-2xl font-serif font-bold">À propos de cette formation</h2>
                <div className="prose dark:prose-invert max-w-none">
                  <p>{course.longDescription}</p>

                  {details?.apprendre && details.apprendre.length > 0 && (
                    <>
                      <h3 className="text-xl font-serif font-bold mt-6">Ce que vous allez apprendre</h3>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
                        {details.apprendre.map((item: ApprendreItem, index: number) => (
                          <li key={index} className="flex items-start">
                            <svg
                              className="w-5 h-5 text-purple-500 mr-2 mt-0.5 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                            {item.learningText}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {details?.publicCible && details.publicCible.length > 0 && (
                    <>
                      <h3 className="text-xl font-serif font-bold mt-6">Public cible</h3>
                      <ul className="mt-4 space-y-2">
                        {details.publicCible.map((item: PublicCibleItem, index: number) => (
                          <li key={index} className="flex items-start">
                            <svg
                              className="w-5 h-5 text-gray-500 mr-2 mt-0.5 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              ></path>
                            </svg>
                            {item.targetText}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="resources" className="mt-6">
              {details?.ressources && details.ressources.length > 0 ? (
                <div className="space-y-4">
                  <h2 className="text-2xl font-serif font-bold">Ressources pédagogiques</h2>
                  <p className="text-gray-500 dark:text-gray-400">
                    Téléchargez les ressources complémentaires pour approfondir votre apprentissage.
                  </p>

                  <div className="grid gap-4 mt-6">
                    {details.ressources.map((resource: Ressource, index: number) => (
                      <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center">
                          <div className="p-2 bg-purple-100 rounded-lg mr-4">
                            {resource.fileType === "pdf" && (
                              <svg
                                className="w-6 h-6 text-purple-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                ></path>
                              </svg>
                            )}
                            {(resource.fileType === "video" || resource.fileType === "mp4") && (
                              <svg
                                className="w-6 h-6 text-purple-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                                ></path>
                              </svg>
                            )}
                            {(!resource.fileType || resource.fileType === "link") && (
                              <svg
                                className="w-6 h-6 text-purple-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                                ></path>
                              </svg>
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium">{resource.name}</h4>
                            <p className="text-sm text-gray-500">{resource.description}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => {
                          if (resource.filePath) {
                            window.open(resource.filePath, '_blank')
                          }
                        }}>
                          <Download className="w-4 h-4 mr-2" />
                          Télécharger
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">Aucune ressource disponible pour cette formation</p>
                </div>
              )}
            </TabsContent>


            <TabsContent value="formateur" className="mt-6">
              {details?.formateur ? (
                <div className="space-y-6">
                  <h2 className="text-2xl font-serif font-bold">À propos du formateur</h2>
                  
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    <div className="flex-shrink-0">
                      <img
                        src={details.formateur.photo || "/avatar.png"}
                        alt={details.formateur.name}
                        width={120}
                        height={120}
                        className="rounded-full object-cover border-4 border-white shadow-lg"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = "/placeholder.svg";
                        }}
                      />
                    </div>
                    
                    <div className="flex-1 space-y-4">
                      <div>
                        <h3 className="text-xl font-bold">{details.formateur.name}</h3>
                        <p className="text-gray-500 dark:text-gray-400">Formateur expert</p>
                      </div>
                      
                      {details.formateur.bio && (
                        <div>
                          <h4 className="font-semibold mb-2">Biographie</h4>
                          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            {details.formateur.bio}
                          </p>
                        </div>
                      )}
                      
                      {details.formateur.socialMedia && (
                        <div>
                          <h4 className="font-semibold mb-3">Suivez-moi</h4>
                          <div className="flex gap-4">
                            {details.formateur.socialMedia.facebook && (
                              <a
                                href={details.formateur.socialMedia.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                                Facebook
                              </a>
                            )}
                            
                            {details.formateur.socialMedia.instagram && (
                              <a
                                href={details.formateur.socialMedia.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors"
                              >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                </svg>
                                Instagram
                              </a>
                            )}
                            
                            {details.formateur.socialMedia.linkedin && (
                              <a
                                href={details.formateur.socialMedia.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
                              >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                                LinkedIn
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="flex flex-col items-center">
                    <img
                      src="/avatar.png"
                      alt="Elille Academy"
                      width={80}
                      height={80}
                      className="rounded-full mb-4"
                    />
                    <h3 className="text-lg font-semibold mb-2">Elille Academy</h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      Formation dispensée par notre équipe d'experts
                    </p>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Related Courses */}
      {similarFormations.length > 0 && (
        <section className="w-full py-12 md:py-24 bg-purple-50 dark:bg-gray-900">
          <div className="container px-4 md:px-6">
            <h2 className="text-2xl font-serif font-bold mb-8">Formations similaires</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {similarFormations.map((course, index) => (
                <a href={`/courses/${course.id}`} key={course.id} className="transition-transform hover:scale-105">
                  <Card className="h-full overflow-hidden border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <CardHeader className="p-0">
                      <div className="relative h-48 w-full group">
                        <img
                          src={course.image || "/placeholder.svg"}
                          alt={course.name}
                          className="object-cover w-full h-full absolute inset-0"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <PlayCircle className="w-16 h-16 text-white opacity-80" />
                        </div>
                        <div className="absolute top-0 right-0 m-2">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/90 text-elille-blue">
                            {course.FormationCategory?.name || 'Formation'}
                          </span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-5">
                      <CardTitle className="text-xl mb-3 line-clamp-2 h-14">{course.name}</CardTitle>
                      <div className="flex flex-wrap gap-3 mt-4">
                        <div className="flex items-center px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full">
                          <Clock className="w-4 h-4 mr-1.5 text-elille-blue" />
                          <span className="text-sm font-medium">{formatDuration(course.totalDuration)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
